import { LibraryConstants } from '@/modules/library/constants'
import { MultiUploadJob } from '@/modules/library/types'
import type { GenerateJigProgress } from '@/services/GenerateJigService'
import { LibraryEntry } from '@/store/modules/teamlibrary/types'
import { Namespace } from '@/store/types'
import { Timing } from '@/utils/timing'
import type { Store } from 'vuex'

declare var consoleError: any
declare var consoleLog: any

const TUS_POLL_INTERVAL_MS = 250
const STATE_READY_TIMEOUT_MS = 5000
const FILE_UPLOAD_AND_JOB_TIMEOUT_MS = 30 * 60 * 1000

const OUTPUT_RESOURCE_IDS_KEYS: string[] = [
  'outputResourceIDs',
  'OutputResourceIDs',
  'outputResourceIds',
  'OutputResourceIds',
]

export type UploadLibraryModelOptions = {
  store: Store<any>
  file: File
  onProgress?: (progress: GenerateJigProgress) => void
}

function toError(error: unknown, fallbackMessage: string): Error {
  if (error instanceof Error) {
    return error
  }
  if (typeof error === 'string' && error.trim().length > 0) {
    return new Error(error)
  }
  return new Error(fallbackMessage)
}

function parsePositiveId(candidate: unknown): number | null {
  if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
    return candidate
  }
  if (typeof candidate === 'string' && candidate.trim().length > 0) {
    const parsed = Number(candidate)
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed
    }
  }
  return null
}

export function extractLibraryEntryId(jobMeta: object | null | undefined): number | null {
  if (!jobMeta || typeof jobMeta !== 'object') {
    return null
  }
  const meta = jobMeta as Record<string, unknown>
  for (const key of OUTPUT_RESOURCE_IDS_KEYS) {
    const outputResourceIDs = meta[key]
    if (Array.isArray(outputResourceIDs) && outputResourceIDs.length > 0) {
      return parsePositiveId(outputResourceIDs[0])
    }
  }
  return null
}

async function loadLibraryEntryById(store: Store<any>, id: number): Promise<LibraryEntry | null> {
  const ok = await store.dispatch('teamlibrary/loadLibraryEntry', { Id: id })
  if (!ok) {
    return null
  }
  return store.state[Namespace.TeamLibrary].libraryEntry as LibraryEntry
}

export async function uploadLibraryModel({ store, file, onProgress }: UploadLibraryModelOptions): Promise<LibraryEntry> {
  if (!file) {
    throw new Error('No file provided for upload.')
  }

  await store.dispatch('library/multiUploadJobsClearAll')
  // uploadFile returns synchronously after kicking off the TUS upload; the upload itself
  // runs through callbacks and updates state via mutations.
  let uploadStartError: Error | null = null
  try {
    const uploadStartResult = store.dispatch('library/uploadFile', { fileToUpload: file }) as Promise<unknown> | undefined
    if (uploadStartResult && typeof uploadStartResult.catch === 'function') {
      void uploadStartResult.catch((error: unknown) => {
        uploadStartError = toError(error, 'Upload did not start.')
      })
    }
  } catch (error) {
    uploadStartError = toError(error, 'Upload did not start.')
  }

  // Wait for the multiUploadJob to be created in state.
  let multiUploadJobIndex = -1
  const stateReadyDeadline = Date.now() + STATE_READY_TIMEOUT_MS
  while (Date.now() < stateReadyDeadline) {
    if (uploadStartError !== null) {
      throw uploadStartError
    }
    const jobs = store.state.library.jobs as MultiUploadJob[]
    if (jobs && jobs.length > 0) {
      multiUploadJobIndex = jobs.length - 1
      break
    }
    await Timing.sleep(50)
  }
  if (multiUploadJobIndex < 0) {
    throw new Error('Upload did not start.')
  }

  // Poll TUS upload progress until the file is fully transferred and the server has
  // returned a Job. Phase 1 (0–40%) is reserved for the file-upload bytes.
  const uploadJobDeadline = Date.now() + FILE_UPLOAD_AND_JOB_TIMEOUT_MS
  while (true) {
    if (uploadStartError !== null) {
      throw uploadStartError
    }
    const jobs = store.state.library.jobs as MultiUploadJob[]
    const muj = jobs[multiUploadJobIndex]
    if (!muj) {
      throw new Error('Upload tracking lost.')
    }
    const tracked = muj.files && muj.files.length > 0 ? muj.files[0] : null
    if (tracked && tracked.errorMsg) {
      throw new Error(tracked.errorMsg)
    }

    if (onProgress && tracked) {
      const filePercent = Math.max(0, Math.min(100, tracked.uploadProgressPercentage || 0))
      onProgress({
        message: 'Uploading model...',
        percentage: Math.round(filePercent * 0.4),
      })
    }

    if (tracked && tracked.finished && muj.job && muj.job.id > 0) {
      break
    }
    if (Date.now() >= uploadJobDeadline) {
      throw new Error('Upload did not finish. Please check your connection and try again.')
    }
    await Timing.sleep(TUS_POLL_INTERVAL_MS)
  }

  if (onProgress) {
    onProgress({
      message: 'Processing model...',
      percentage: 40,
    })
  }

  // Phase 2: backend pipeline. The action polls /jobs/v1/{id} until terminal.
  await store.dispatch('library/pollJobUntilFinalResultAsync', multiUploadJobIndex)

  const jobsAfter = store.state.library.jobs as MultiUploadJob[]
  const muj = jobsAfter[multiUploadJobIndex]
  if (!muj || !muj.job) {
    throw new Error('Upload job missing after processing.')
  }
  if (muj.job.status === LibraryConstants.JobStatus.Failed || muj.job.errorMsg) {
    throw new Error(muj.job.errorMsg || 'Model processing failed.')
  }
  if (muj.job.status !== LibraryConstants.JobStatus.Finished) {
    throw new Error('Model processing did not complete.')
  }

  if (onProgress) {
    onProgress({
      message: 'Loading library entry...',
      percentage: 55,
    })
  }

  // Phase 3: hydrate the exact LibraryEntry produced by the upload job so it can be
  // fed to GenerateJigService.
  const libraryEntryIdFromMeta = extractLibraryEntryId(muj.job.jobMeta)
  consoleLog('uploadLibraryModel: finished job', { jobId: muj.job.id, jobMeta: muj.job.jobMeta, outputResourceId: libraryEntryIdFromMeta })
  if (libraryEntryIdFromMeta === null) {
    consoleError(
      'Could not resolve uploaded library entry id from jobMeta.outputResourceIDs. jobMeta:',
      muj.job.jobMeta,
      'fileName:',
      file.name
    )
    throw new Error('Could not resolve uploaded library entry.')
  }

  const libraryEntry = await loadLibraryEntryById(store, libraryEntryIdFromMeta)

  if (!libraryEntry || !libraryEntry.Item) {
    consoleError(
      'Could not load uploaded library entry. libraryEntryId:',
      libraryEntryIdFromMeta,
      'jobMeta:',
      muj.job.jobMeta,
      'fileName:',
      file.name
    )
    throw new Error('Could not resolve uploaded library entry.')
  }

  if (onProgress) {
    onProgress({
      message: 'Library entry ready.',
      percentage: 60,
    })
  }

  return libraryEntry
}
